 import React, { useEffect, useRef, useState } from 'react'
import { linkPath, linkPathImageChat } from '../../../share/helper'
import { request } from '../../../share/request'
import { getCurrentUser } from '../../../share/getProfile'
import { Button, Modal } from 'antd'
import { IoAddCircle } from 'react-icons/io5'
import CreateGroupModal from '../GroupChat/CreateGroupModal'

export default function SidebarChat({ 
    user, 
    allUsers = [], 
    chatTarget, 
    unreadCounts = {}, 
    onChat, 
    recentChats = [], 
    groups = [],
    onLogout, 
    onSettings, 
    onlineUsers = new Set(), 
    presence = { ids: new Set(), names: new Set() }, 
    search: propSearch, 
    onSearchChange 
}) {
    const [open, setOpen] = React.useState(false)
    const [internalSearch, setInternalSearch] = React.useState('')
    const search = (typeof propSearch !== 'undefined') ? propSearch : internalSearch
    const setSearch = onSearchChange || setInternalSearch
    const [localUsers, setLocalUsers] = useState(allUsers || [])
    const searchTimeoutRef = useRef(null)
    const [showDropdown, setShowDropdown] = useState(false)
    const searchRef = useRef(null)
    
    // 🔥 SEPARATE base URLs for private and group avatars
    const PRIVATE_BASE = linkPath      // For user avatars
    const GROUP_BASE = linkPathImageChat // For group avatars
    
    const searchTerm = (search || '').trim().toLowerCase()
    
    const formatTime = (t) => {
        try {
            if (!t) return ''
            const d = new Date(t)
            if (isNaN(d.getTime())) return String(t)
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
        } catch (e) { return String(t) }
    }
    
    const matchesSearch = (u) => {
        if (!searchTerm) return true
        if (!u) return false
        const fields = [u.fullname, u.username, u.staffname_eng, u.frontname, u.position, u.department_name, u.idnumber]
        const idstr = (u && String(u.id)) || ''
        for (const f of fields) {
            if (f && String(f).toLowerCase().includes(searchTerm)) return true
        }
        if (idstr && idstr.toLowerCase().includes(searchTerm)) return true
        return false
    }

    // 🔥 Build recent chats from both private and groups
    const buildRecentChats = () => {
        // 🔥 Private items - SHOW ALL chats
        const privateItems = (recentChats || [])
            .filter(rc => {
                const u = rc.user || localUsers.find(x => String(x.id) === String(rc.other)) || { id: rc.other }
                const isNotCurrentUser = String((u && u.id) || '') !== String(user && user.id)
                return isNotCurrentUser && matchesSearch(u)
            })
            .map(rc => ({
                ...rc,
                type: 'private',
                id: rc.room || String(rc.other),
                displayName: rc.user?.fullname || rc.user?.username || `User ${rc.other}`,
                avatar: rc.user?.avatar,
                is_group: false,
                user: rc.user,
                lastMessagePreview: rc.lastMessagePreview || 'No messages yet',
                time: rc.time || null
            }));

        // 🔥 Group items - SHOW ALL groups
        const groupItems = (groups || [])
            .filter(g => g && g.id)
            .filter(g => {
                if (!searchTerm) return true
                return String(g.name || '').toLowerCase().includes(searchTerm) ||
                       String(g.id).toLowerCase().includes(searchTerm)
            })
            .map(g => ({
                room: g.room_id || `group_${g.id}`,
                other: g.id,
                user: {
                    id: g.id,
                    fullname: g.name,
                    username: g.name,
                    avatar: g.avatar_url || g.avatar,
                    is_group: true,
                    group_id: g.id,
                    room_id: g.room_id,
                    member_count: g.member_count
                },
                lastMessagePreview: g.last_message || 'Group created',
                time: g.last_message_time || g.created_at || new Date().toISOString(),
                unread: unreadCounts[String(g.id)] || 0,
                type: 'group',
                id: String(g.id),
                displayName: g.name,
                avatar: g.avatar_url || g.avatar,
                is_group: true,
                group_id: g.id,
                room_id: g.room_id,
                member_count: g.member_count
            }));

        const all = [...privateItems, ...groupItems];
        all.sort((a, b) => {
            const timeA = a.time ? new Date(a.time).getTime() : 0;
            const timeB = b.time ? new Date(b.time).getTime() : 0;
            return timeB - timeA;
        });

        return all;
    };

    const filteredRecent = buildRecentChats();

    const recentIds = new Set((filteredRecent || []).map(rc => {
        if (rc.is_group) {
            return `group_${rc.other}`;
        }
        const ru = rc.user || localUsers.find(x => String(x.id) === String(rc.other)) || { id: rc.other };
        return String((ru && ru.id) || rc.other || '');
    }));

    const filteredAll = (localUsers || [])
        .filter(u => u && String(u.id) !== String(user && user.id))
        .filter(u => !recentIds.has(String(u.id)))
        .filter(u => matchesSearch(u));

    const [ModalCreateGroup, setModalCreateGroup] = useState(false)
    
    const btn_addChatStaff = () => {
        setModalCreateGroup(true)
    }

    const handleGroupCreated = (groupData) => {
        console.log('Group created:', groupData)
        if (onChat) {
            onChat({
                ...groupData,
                is_group: true
            })
        }
    }

    // Helper function to get image URL
    const getImageUrl = (image, baseUrl) => {
        if (!image) return null
        if (String(image).startsWith('http')) return image
        if (String(image).startsWith('/')) return baseUrl + image.replace(/^\//, '')
        return baseUrl + image
    }

    return (
        <div className="sidebar-container">
            <div className="sidebar-top">
                <div className="sidebar-user" style={{borderWidth:2}}>
                    <div className="avatar">
                        {user && user.image ? (
                            <img 
                                src={getImageUrl(user.image, PRIVATE_BASE)} 
                                alt={user.fullname || user.username} 
                                style={{width:'100%', height:'100%', objectFit:'cover'}} 
                            />
                        ) : (
                            ((user && (user.fullname || user.username)) || 'SC').slice(0,2).toUpperCase()
                        )}
                    </div>
                    <div>
                        <div className="sidebar-title">Chats</div>
                        <div className="sidebar-sub">{user ? (user.fullname || user.username) : `${allUsers.length} contacts`}</div>
                    </div>
                </div>
            </div>

            {/* Search box */}
            <div className="search-box" ref={searchRef}>
                <div style={{flex:1, flexDirection:'column', justifyItems:'center', alignItems:'center', gap:5, position:'relative'}}>
                    <input   
                        placeholder="ស្វែងរកឈ្មោះបុគ្គលិក...."   
                        className='search-input'
                        value={search} 
                        onChange={(e) => {
                            setSearch(e.target.value)
                            if (e.target.value.trim().length > 0) {
                                setShowDropdown(true)
                            } else {
                                setShowDropdown(false)
                            }
                        }}
                        onFocus={() => {
                            if (search && search.trim().length > 0) {
                                setShowDropdown(true)
                            }
                        }}
                    />
                    
                    {showDropdown && filteredAll && filteredAll.length > 0 && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            marginTop: '4px'
                        }}>
                            {filteredAll.map(u => (
                                <div 
                                    key={u.id} 
                                    onClick={() => {
                                        onChat(u)
                                        setShowDropdown(false)
                                        setSearch('')
                                    }} 
                                    style={{
                                        padding: '10px 15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f0f0f0',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                    <div className="avatar-small" style={{width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', flexShrink: 0}}>
                                        {u.avatar ? (
                                            <img 
                                                src={getImageUrl(u.avatar, PRIVATE_BASE)} 
                                                alt={u.fullname || u.username} 
                                                style={{width:'100%', height:'100%', objectFit:'cover'}} 
                                            />
                                        ) : (
                                            <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'#e0e0e0', color:'#666', fontWeight:'bold'}}>
                                                {((u.fullname || u.username)||'U').slice(0,1).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{flex: 1}}>
                                        <div style={{fontWeight: '500', fontSize: '14px'}}>{u.fullname || u.username}</div>
                                        <div style={{fontSize: '12px', color: '#999'}}>{u.position || u.department_name || `ID ${u.id}`}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {showDropdown && search && search.trim().length > 0 && filteredAll && filteredAll.length === 0 && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '20px',
                            textAlign: 'center',
                            color: '#999',
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            marginTop: '4px'
                        }}>
                            មិនមានបុគ្គលិក
                        </div>
                    )}
                    
                    <Button   
                        icon={<IoAddCircle color='green' size={24}/>} 
                        onClick={btn_addChatStaff} 
                        style={{justifyContent:'center', alignItems:'center', alignContent:'center'}}
                    />
                </div>
            </div>

            <div className="recent-section" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
                {filteredRecent && filteredRecent.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div className="recent-title" style={{ flexShrink: 0 }}>Recent</div>
                        <div className="recent-list recent-list-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                            {filteredRecent.map(rc => {
                                if (rc.is_group) {
                                    // 🔥 Render group item - using GROUP_BASE
                                    return (
                                        <div 
                                            key={rc.room} 
                                            className={`recent-item ${chatTarget && String(chatTarget.id) === String(rc.group_id) && chatTarget.is_group ? 'active' : ''}`} 
                                            onClick={() => onChat({
                                                id: rc.group_id,
                                                fullname: rc.displayName,
                                                username: rc.displayName,
                                                avatar: rc.avatar,
                                                is_group: true,
                                                room_id: rc.room_id,
                                                member_count: rc.member_count
                                            })}
                                        >
                                            <div className="recent-left">
                                                <div className="avatar-small" style={{ backgroundColor: '#e8f0fe', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {rc.avatar ? (
                                                        <div style={{position:'relative', display:'inline-block', width: '100%', height: '100%'}}>
                                                            <img 
                                                                src={getImageUrl(rc.avatar, GROUP_BASE)} 
                                                                alt={rc.displayName} 
                                                                style={{width:'100%', height:'100%', objectFit:'cover', borderRadius: 8}} 
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#4CAF50', color: '#fff', borderRadius: 8, fontSize: 18, fontWeight: 'bold' }}>
                                                            {rc.displayName ? rc.displayName.slice(0,1).toUpperCase() : 'G'}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="user-meta">
                                                    <div className="user-name" style={{fontFamily:'Content'}}>
                                                        {rc.displayName || 'Group'}
                                                        <span style={{ fontSize: 10, color: '#1a73e8', marginLeft: 8, background: '#e8f0fe', padding: '0 8px', borderRadius: 10 }}>
                                                            Group
                                                        </span>
                                                    </div>
                                                    <div className="user-sub" style={{fontFamily:'Content'}}>
                                                        {rc.lastMessagePreview || 'No messages yet'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="right-meta">
                                                {rc.unread > 0 ? (
                                                    <div className="badge">{rc.unread}</div>
                                                ) : null}
                                                <div className="time">{formatTime(rc.time)}</div>
                                            </div>
                                        </div>
                                    );
                                }

                                // 🔥 Private chat item - using PRIVATE_BASE
                                const ru = rc.user || allUsers.find(x => String(x.id) === String(rc.other)) || { id: rc.other, username: String(rc.other) };
                                const rid = String(ru.id || rc.other || rc.room || '');
                                return (
                                    <div key={rc.room || rid} className={`recent-item ${chatTarget && String(chatTarget.id)===String(ru.id) && !chatTarget.is_group ? 'active' : ''}`} onClick={() => onChat(ru)}>
                                        <div className="recent-left">
                                            <div className="avatar-small">
                                                {ru && ru.avatar ? (
                                                    <div style={{position:'relative', display:'inline-block'}}>
                                                        <img 
                                                            src={getImageUrl(ru.avatar, PRIVATE_BASE)} 
                                                            alt={ru.fullname || ru.username} 
                                                            style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:6}} 
                                                        />
                                                        <span style={{position:'absolute', right:2, bottom:2, width:12, height:12, borderRadius:12, border:'2px solid #fff', boxShadow:'0 0 0 2px rgba(0,0,0,0.06)', background: ((onlineUsers && (onlineUsers.has(String((ru && ru.id) || '')) || (ru && ru.username && onlineUsers.has(String(ru.username))))) ? '#2ecc71' : '#f1c40f')}} />
                                                    </div>
                                                ) : (
                                                    <div style={{position:'relative', display:'inline-block'}}>
                                                        {((ru && (ru.fullname || ru.username))||'U').slice(0,1).toUpperCase()}
                                                        <span style={{position:'absolute', right:2, bottom:2, width:12, height:12, borderRadius:12, border:'2px solid #fff', boxShadow:'0 0 0 2px rgba(0,0,0,0.06)', background: ((onlineUsers && (onlineUsers.has(String((ru && ru.id) || '')) || (ru && ru.username && onlineUsers.has(String(ru.username))))) ? '#2ecc71' : '#f1c40f')}} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="user-meta">
                                                <div className="user-name" style={{fontFamily:'Content'}}>{ru && (ru.fullname || ru.username)}</div>
                                                <div className="user-sub" style={{fontFamily:'Content'}}>{rc.lastMessagePreview || 'No messages'}</div>
                                            </div>
                                        </div>
                                        <div className="right-meta">
                                            {unreadCounts && unreadCounts[String((ru && ru.id) || '')] > 0 ? (
                                                <div className="badge">{unreadCounts[String((ru && ru.id) || '')]}</div>
                                            ) : null}
                                            <div className="time">{formatTime(rc.time)}</div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ) : (
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%', 
                        color: '#999', 
                        fontSize: 14,
                        flex: 1,
                        padding: '20px'
                    }}>
                        <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
                        <div>No recent chats</div>
                        <div style={{ fontSize: 12, color: '#bbb', marginTop: 4 }}>
                            Start a conversation or create a group
                        </div>
                    </div>
                )}
            </div>

            <CreateGroupModal
                open={ModalCreateGroup}
                onClose={() => setModalCreateGroup(false)}
                allUsers={allUsers}
                currentUser={user}
                onGroupCreated={handleGroupCreated}
                linkPath={PRIVATE_BASE}
            />
        </div>
    )
}